
import { useState } from 'react';
import { Search, Dice6, Users, Clock, ChevronDown, Filter } from 'lucide-react';
import { Game } from '../../../types';
import { mockGames } from '../../../mocks/games';
import Card from '../../../components/base/Card';
import Input from '../../../components/base/Input';
import Button from '../../../components/base/Button';
import { GameCardSkeleton } from '../../../components/base/LoadingSkeleton';

interface GameListProps {
  onGameSelect: (game: Game) => void;
  selectedGameId?: string;
}

export default function GameList({ onGameSelect, selectedGameId }: GameListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(10);
  const [selectedTag, setSelectedTag] = useState<string>('');

  // 利用可能なタグを取得
  const availableTags = Array.from(new Set(mockGames.flatMap(game => game.tags || [])));

  const filteredGames = mockGames.filter(
    (game) =>
      game.status === 'published' &&
      game.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (selectedTag === '' || (game.tags && game.tags.includes(selectedTag)))
  );

  const displayedGames = filteredGames.slice(0, displayCount);
  const hasMore = displayCount < filteredGames.length;

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + 10);
  };

  // Simple error handling for unexpected states (e.g., empty mock data)
  if (!Array.isArray(mockGames)) {
    return (
      <Card className="p-12 text-center bg-white/80 backdrop-blur-sm">
        <p className="text-red-500">ゲームデータの取得に失敗しました。</p>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <Input
            placeholder="ゲーム名で検索"
            icon={<Search className="h-4 w-4 text-gray-400" />}
            disabled
          />
        </div>
        <div className="grid grid-cols-1 gap-6">
          <GameCardSkeleton count={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="relative">
          <Input
            placeholder="ゲーム名で検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search className="h-5 w-5 text-gray-400" />}
            className="bg-white/80 backdrop-blur-sm border-gray-200 focus:border-blue-400 focus:ring-blue-400/20"
          />
        </div>

        {/* タグフィルター */}
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-gray-500" />
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedTag === '' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setSelectedTag('')}
              className="whitespace-nowrap"
            >
              すべて
            </Button>
            {availableTags.map((tag) => (
              <Button
                key={tag}
                variant={selectedTag === tag ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setSelectedTag(tag)}
                className="whitespace-nowrap"
              >
                {tag}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {filteredGames.length === 0 ? (
        <Card className="p-12 text-center bg-white/80 backdrop-blur-sm">
          <div className="text-gray-500">
            {searchQuery || selectedTag ? (
              <>
                <Dice6 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">
                  検索結果が見つかりません
                </p>
                <p className="text-sm">
                  {searchQuery && selectedTag 
                    ? `「${searchQuery}」かつ「${selectedTag}」に一致するゲームがありません`
                    : searchQuery 
                    ? `「${searchQuery}」に一致するゲームがありません`
                    : `「${selectedTag}」タグのゲームがありません`
                  }
                </p>
              </>
            ) : (
              <>
                <Dice6 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">まだゲームがありません</p>
                <p className="text-sm">管理画面から追加してください</p>
              </>
            )}
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6">
            {displayedGames.map((game) => (
              <Card
                key={game.id}
                hover
                className={`p-6 transition-all duration-300 bg-white/90 backdrop-blur-sm border-gray-200 hover:shadow-xl hover:scale-[1.02] cursor-pointer ${
                  selectedGameId === game.id
                    ? 'ring-2 ring-blue-500 shadow-lg bg-blue-50/50'
                    : 'hover:border-blue-300'
                }`}
                onClick={() => onGameSelect(game)}
              >
                <div className="flex gap-6">
                  <div className="w-32 h-20 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
                    {/* Provide a fallback image if coverImage is missing */}
                    <img
                      src={game.coverImage ?? '/fallback-cover.png'}
                      alt={game.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900 truncate">{game.name}</h3>
                      <div className="flex gap-2">
                        {/* タグ表示 */}
                        {game.tags && game.tags.map((tag) => (
                          <span 
                            key={tag}
                            className={`text-white text-xs px-3 py-1 rounded-full font-medium ${
                              tag === 'TRPG' 
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                                : 'bg-gradient-to-r from-blue-500 to-green-500'
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                        {game.id.startsWith('demo-') && (
                          <span className="bg-gray-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                            DEMO
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                      {game.summary}
                    </p>

                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{game.metadata?.playerCount ?? 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{game.metadata?.playTime ?? 'N/A'}</span>
                      </div>
                    </div>

                    <Button
                      variant={selectedGameId === game.id ? 'primary' : 'secondary'}
                      size="sm"
                      className={`whitespace-nowrap ${
                        selectedGameId === game.id
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onGameSelect(game);
                      }}
                    >
                      {selectedGameId === game.id ? 'チャット中' : 'チャット開始'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                variant="secondary"
                onClick={handleLoadMore}
                className="bg-white/80 backdrop-blur-sm border-gray-200 hover:bg-gray-50 text-gray-700 px-8 py-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
              >
                <ChevronDown className="h-4 w-4 mr-2" />
                さらに表示 ({filteredGames.length - displayCount}件)
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}